import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import axios from 'axios';
import { toast } from 'sonner';

import {
  ArrowLeft,
  Loader2,
  ShieldCheck,
  UserRoundCheck,
} from 'lucide-react';

import { API } from '../App';
import HamburgerMenu from '../components/HamburgerMenu';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';


const ROLE_LABELS = {
  coach: 'Coach',
  assistant_coach: 'Assistant Coach',
  nutritionist: 'Nutritionist',
  healthcare_professional: 'Healthcare Professional',
};


export default function PrivacySharing() {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [
    savingRecipient,
    setSavingRecipient,
  ] = useState(null);


  const load = useCallback(async () => {
    setLoading(true);

    try {
      const res = await axios.get(
        `${API}/orgs/${orgId}/consents/me`
      );

      setData(
        res.data
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail
        || 'Could not load privacy settings.'
      );
    } finally {
      setLoading(false);
    }
  }, [orgId]);


  useEffect(() => {
    load();
  }, [load]);


  const setScope = async (
    recipient,
    scope,
    allow,
  ) => {
    const current = new Set(
      recipient.current_scopes
      || []
    );

    if (allow) {
      current.add(
        scope
      );
    } else {
      current.delete(
        scope
      );
    }

    setSavingRecipient(
      recipient.user_id
    );

    try {
      const res = await axios.put(
        `${API}/orgs/${orgId}/consents/${recipient.user_id}`,
        {
          scopes: [
            ...current,
          ],
        }
      );

      setData((existing) => ({
        ...existing,

        consent_version:
          res.data.consent_version,

        scope_definitions:
          res.data.scope_definitions,

        recipients:
          res.data.recipients,
      }));

      toast.success(
        allow
          ? 'Permission allowed.'
          : 'Permission set to private.'
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail
        || 'Could not update privacy setting.'
      );
    } finally {
      setSavingRecipient(
        null
      );
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900">
        <HamburgerMenu />

        <div className="flex justify-center py-32">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
        </div>
      </div>
    );
  }


  if (!data) {
    return null;
  }


  return (
    <div
      className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900"
      data-testid="privacy-sharing-page"
    >
      <HamburgerMenu />

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <Button
          variant="outline"
          onClick={() => navigate('/teams')}
          className="glass border-stone-700 text-stone-300 hover:bg-stone-800 mb-5"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Teams
        </Button>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-amber-400 mb-1">
            {data.organization?.name}
          </p>

          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wider">
            PRIVACY &amp; SHARING
          </h1>

          <p className="text-sm text-stone-400 mt-2 max-w-2xl">
            You control which named professionals may receive each category
            of your sensitive Anvil &amp; Ash information. Organization
            membership by itself does not grant these permissions.
          </p>
        </div>

        <Card className="glass border-stone-800 p-4 mb-5">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />

            <div>
              <p className="text-sm font-bold text-white">
                Organization administration is separate from professional access.
              </p>

              <p className="text-xs text-stone-500 mt-1">
                An organization administrator can manage billing, membership,
                teams, staff, and organization settings. That administrative
                role does not itself grant permission to see your sensitive
                athlete information.
              </p>
            </div>
          </div>
        </Card>

        {(data.recipients || []).length === 0 ? (
          <Card className="glass border-stone-800 p-6 text-center">
            <UserRoundCheck className="w-7 h-7 text-emerald-400 mx-auto mb-3" />

            <p className="text-white font-medium">
              No named professional is currently assigned to you.
            </p>

            <p className="text-sm text-stone-500 mt-2">
              There are currently no professional organization members who
              can receive sensitive data through your team relationship.
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {data.recipients.map((recipient) => (
              <Card
                key={recipient.user_id}
                className="glass border-stone-800 p-5"
                data-testid={`privacy-recipient-${recipient.user_id}`}
              >
                <div className="mb-4">
                  <p className="text-white font-bold">
                    {recipient.username}
                  </p>

                  <p className="text-xs uppercase tracking-wider text-amber-400">
                    {ROLE_LABELS[recipient.role] || recipient.role}
                  </p>
                </div>

                <div className="space-y-2">
                  {(recipient.allowed_scopes || []).map((scope) => {
                    const definition = (
                      data.scope_definitions?.[scope]
                      || {}
                    );

                    const allowed = (
                      recipient.current_scopes
                      || []
                    ).includes(scope);

                    const serviceRequired = (
                      recipient.required_scopes
                      || []
                    ).includes(scope);

                    const saving = (
                      savingRecipient
                      === recipient.user_id
                    );

                    return (
                      <div
                        key={scope}
                        className="rounded-md border border-stone-800 bg-stone-900/50 p-3"
                      >
                        <div className="flex flex-wrap items-start gap-3">
                          <div className="flex-1 min-w-[180px]">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-medium text-stone-200">
                                {definition.label || scope}
                              </p>

                              {serviceRequired && (
                                <span className="text-[10px] uppercase tracking-wider text-amber-400">
                                  Service may require this
                                </span>
                              )}
                            </div>

                            <p className="text-xs text-stone-500 mt-1">
                              {definition.description}
                            </p>
                          </div>

                          <div className="flex rounded-md overflow-hidden border border-stone-700">
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => setScope(
                                recipient,
                                scope,
                                false,
                              )}
                              className={`px-3 py-2 text-xs font-bold ${
                                !allowed
                                  ? 'bg-stone-700 text-white'
                                  : 'bg-stone-950 text-stone-500 hover:text-white'
                              }`}
                              data-testid={`private-${recipient.user_id}-${scope}`}
                            >
                              Private
                            </button>

                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => setScope(
                                recipient,
                                scope,
                                true,
                              )}
                              className={`px-3 py-2 text-xs font-bold ${
                                allowed
                                  ? 'bg-emerald-800 text-white'
                                  : 'bg-stone-950 text-stone-500 hover:text-white'
                              }`}
                              data-testid={`allow-${recipient.user_id}-${scope}`}
                            >
                              Allow
                            </button>
                          </div>
                        </div>

                        {serviceRequired && !allowed && (
                          <p className="text-xs text-amber-500 mt-2">
                            This professional normally needs this category
                            for the service they provide. Revoking it may
                            disable related coaching features.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}

        <p className="text-xs text-stone-600 mt-6 leading-relaxed">
          You can change these permissions at any time. Each permission
          change is stored with an audit event.
        </p>
      </div>
    </div>
  );
}
